import * as React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { useI18n } from "../i18n/I18nProvider";


const content = {
    en: {
      title: "Terms of Service",
      updated: "Last updated",

      intro:
        "These Terms of Service govern your access to and use of takeKONTROL. By creating an account or using the platform, you agree to these terms. If you do not agree, you must not use the service.",

      sections: [
        {
          title: "1. Scope of the Service",
          body: [
            "takeKONTROL is a digital platform designed to support individuals and organizations in improving preparedness, resilience, and crisis awareness.",
            "The platform provides structured questionnaires, behavioral inputs, scoring systems, and action-based modules to guide users in assessing their readiness and identifying improvement areas.",
            "All outputs generated by the platform are informational in nature and are not intended to replace professional, legal, medical, or emergency advice.",
          ],
        },
        {
          title: "2. User Accounts and Responsibilities",
          body: [
            "To access the service, users must create an account linked to a valid email address.",
            "Users are responsible for maintaining the confidentiality of their login credentials and for all activities under their account.",
            "Users agree to provide accurate, complete, and up-to-date information during registration and profile updates.",
            "Unauthorized use of accounts or attempts to access other users' data is strictly prohibited.",
          ],
        },
        {
          title: "3. Organizational and Employer Context",
          body: [
            "The platform may be used within an organizational context, where users are associated with a company or institution.",
            "In such cases, certain data (e.g., aggregated insights, participation, or scores) may be visible to authorized roles within the organization, such as managers or administrators.",
            "Users acknowledge that their participation may be part of a structured company program (e.g., workshops, assessments, preparedness initiatives).",
          ],
        },
        {
          title: "4. Data Input and Generated Outputs",
          body: [
            "Users provide input through questionnaires, assessments, and interactions with modules.",
            "Based on this input, the system generates scores, classifications, and recommendations.",
            "These outputs are based on algorithmic processing and predefined logic models.",
            "The accuracy and usefulness of outputs depend on the correctness and completeness of user-provided data.",
          ],
        },
        {
          title: "5. Scoring Systems and KONTROL Score Disclaimer",
          body: [
            "The platform generates internal scoring metrics such as kontrol scores and Personal Risk Indicators (KONTROL Score).",
            "These scores are estimations intended to provide guidance and awareness, not definitive evaluations.",
            "They do not represent guarantees of safety, preparedness, or risk level.",
            "Users must not rely solely on these scores when making decisions in real-world situations.",
          ],
        },
        {
          title: "6. Acceptable Use",
          body: [
            "Users agree to use the platform only for lawful purposes and in accordance with these Terms.",
            "It is prohibited to misuse the system, including attempting to manipulate scoring mechanisms, access unauthorized data, or disrupt platform functionality.",
            "Any abuse of the platform may result in suspension or termination of the account.",
          ],
        },
        {
          title: "7. Payments and Subscriptions",
          body: [
            "Certain features of the platform may be subject to payment or subscription.",
            "Payments are processed via third-party providers such as Stripe.",
            "takeKONTROL does not store full payment details and is not responsible for third-party payment processing systems.",
          ],
        },
        {
          title: "8. Account Deletion and Data Removal",
          body: [
            "Users may delete their account at any time through the platform.",
            "Upon deletion, personal data will be removed unless retention is required by law or necessary for legitimate business purposes.",
            "Some anonymized or aggregated data may be retained for analytical purposes.",
          ],
        },
        {
          title: "9. Availability and Service Changes",
          body: [
            "The platform is provided on an 'as is' and 'as available' basis.",
            "We do not guarantee uninterrupted availability or error-free operation.",
            "We reserve the right to modify, suspend, or discontinue parts of the service at any time.",
          ],
        },
        {
          title: "10. Limitation of Liability",
          body: [
            "takeKONTROL is not liable for any direct or indirect damages arising from the use or inability to use the platform.",
            "This includes decisions made based on platform outputs, recommendations, or scores.",
            "Users remain fully responsible for their real-world actions and decisions.",
          ],
        },
        {
          title: "11. Changes to Terms",
          body: [
            "We may update these Terms of Service from time to time.",
            "Continued use of the platform after changes constitutes acceptance of the updated terms.",
          ],
        },
        {
          title: "12. Contact",
          body: [
            "For questions regarding these Terms, please contact: info@takekontrol.de",
          ],
        },
      ],
    },

    de: {
      title: "Nutzungsbedingungen",
      updated: "Zuletzt aktualisiert",

      intro:
        "Diese Nutzungsbedingungen regeln den Zugang und die Nutzung von takeKONTROL. Mit der Nutzung stimmen Sie diesen Bedingungen zu.",

      sections: [
        {
          title: "1. Leistungsumfang",
          body: [
            "takeKONTROL ist eine digitale Plattform zur Förderung von Resilienz, Vorbereitung und Krisenbewusstsein.",
            "Die Plattform stellt Fragebögen, Bewertungsmodelle und Handlungsempfehlungen bereit.",
            "Alle Ergebnisse dienen ausschließlich Informationszwecken.",
          ],
        },
        {
          title: "2. Benutzerkonten",
          body: [
            "Ein Benutzerkonto ist erforderlich.",
            "Sie sind für die Sicherheit Ihrer Zugangsdaten verantwortlich.",
            "Angaben müssen korrekt und aktuell sein.",
          ],
        },
        {
          title: "3. Nutzung im Unternehmen",
          body: [
            "Die Plattform kann im Unternehmenskontext genutzt werden.",
            "Bestimmte Daten können für autorisierte Rollen sichtbar sein.",
          ],
        },
        {
          title: "4. Dateneingaben und Ergebnisse",
          body: [
            "Eingaben werden zur Generierung von Scores genutzt.",
            "Ergebnisse basieren auf algorithmischer Verarbeitung.",
          ],
        },
        {
          title: "5. Score-Haftungsausschluss",
          body: [
            "Scores sind Schätzwerte und keine Garantien.",
            "Sie dürfen nicht als alleinige Entscheidungsgrundlage dienen.",
          ],
        },
        {
          title: "6. Nutzung",
          body: [
            "Nur rechtmäßige Nutzung ist erlaubt.",
          ],
        },
        {
          title: "7. Zahlungen",
          body: [
            "Kostenpflichtige Funktionen möglich.",
          ],
        },
        {
          title: "8. Löschung",
          body: [
            "Konten können gelöscht werden.",
          ],
        },
        {
          title: "9. Verfügbarkeit",
          body: [
            "Keine Garantie für ständige Verfügbarkeit.",
          ],
        },
        {
          title: "10. Haftung",
          body: [
            "Keine Haftung für Schäden durch Nutzung.",
          ],
        },
        {
          title: "11. Änderungen",
          body: [
            "Änderungen jederzeit möglich.",
          ],
        },
        {
          title: "12. Kontakt",
          body: ["info@takekontrol.de"],
        },
      ],
    },
  };


export default function TermsOfService() {
  const { lang } = useI18n();
  const t = content[lang === "de" ? "de" : "en"];

  return (
    <Box maxWidth={900} mx="auto" p={4}>
      <Stack spacing={3}>
        <Typography variant="h4">{t.title}</Typography>

        <Typography variant="body2" color="text.secondary">
          {t.updated}: {new Date().toLocaleDateString()}
        </Typography>

        <Typography>{t.intro}</Typography>

        {t.sections.map((section, i) => (
          <Box key={i}>
            <Typography variant="h6">{section.title}</Typography>
            {section.body.map((line, j) => (
              <Typography key={j}>{line}</Typography>
            ))}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}